import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { waLink, WA_DEFAULT_MSG } from "../lib/site";

const NAV_LINKS = [
  { to: "/", label: "الرئيسية", id: "nav-link-home" },
  { to: "/#tech", label: "تقنية الحساس", id: "nav-link-tech" },
  { to: "/#services", label: "خدماتنا", id: "nav-link-services" },
  { to: "/#catalog", label: "المعرض", id: "nav-link-catalog" },
  { to: "/#finder", label: "اعرف حساسك", id: "nav-link-finder" },
  { to: "/faq", label: "الأسئلة الشائعة", id: "nav-link-faq" },
  { to: "/contact", label: "تواصل معنا", id: "nav-link-contact" },
];

const ALL_PAGES = [
  { to: "/", label: "الرئيسية" },
  { to: "/sensors", label: "حساسات الكفرات" },
  { to: "/programming", label: "برمجة حساسات TPMS" },
  { to: "/replacement", label: "استبدال الحساسات" },
  { to: "/inspection", label: "فحص حساسات الكفرات" },
  { to: "/compatibility", label: "السيارات والحساسات المتوافقة" },
  { to: "/about", label: "من نحن" },
  { to: "/faq", label: "الأسئلة الشائعة" },
  { to: "/contact", label: "اتصل بنا" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
        scrolled
          ? "bg-[#08090C]/85 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" aria-label="حساسات كفرات الرياض - الرئيسية">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="التنقل الرئيسي">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              href={l.to}
              data-testid={l.id}
              className="text-sm text-slate-300 hover:text-white transition-colors relative after:absolute after:-bottom-1 after:right-0 after:h-px after:w-0 hover:after:w-full after:bg-red-500 after:transition-all"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink(WA_DEFAULT_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold px-4 py-2.5 rounded-full shadow-[0_4px_20px_rgba(16,185,129,0.35)] transition-all active:scale-[0.97]"
          >
            <MessageCircle size={16} />
            واتساب
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "إغلاق القائمة" : "فتح القائمة"}
            className="lg:hidden text-white p-2 rounded-lg border border-white/10 bg-white/5"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A0C10]/97 backdrop-blur-xl border-t border-white/10"
            aria-label="قائمة الجوال"
          >
            <div className="px-6 py-6 grid grid-cols-2 gap-x-6 gap-y-4">
              {ALL_PAGES.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  data-testid={`mobile-nav-${p.to === "/" ? "home" : p.to.slice(1)}`}
                  className="text-sm text-slate-200 hover:text-red-400 transition-colors py-1 border-b border-white/5 pb-3"
                >
                  {p.label}
                </Link>
              ))}
              <a
                href={waLink(WA_DEFAULT_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-whatsapp"
                className="col-span-2 mt-2 inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold px-4 py-3 rounded-xl"
              >
                <MessageCircle size={18} />
                استفسر عبر واتساب
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
